import {
  Key,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';

import { removeHiddenColumnsFromFilters } from 'utils/removeHiddenColumnsFromFilters';
import { UseOpQueryOptions } from 'utils/customHooks/useOpQuery';

import { OpTableCore } from '../OpTableCore/OpTableCore';
import {
  AllowExport,
  CommonTableProps,
  OpTableRawColumnType,
  OpTableRecordType,
} from '../OpTableCore/types';
import {
  BatchActions,
  GlobalSearch,
  ShowHideColumnsButton,
  TableHeader,
} from '../OpTableCore/components';
import { TablePagination } from '../OpTableCore/components/TablePagination';
import { createTableWidth } from '../OpTableCore/helpers';
import { getStateToPersist } from '../OpTable/uiPersistence';
import { OnShowHideColumns } from '../OpTableCore/components/ShowHideColumnsButton';

import { useDataFetchTable } from './useDataFetchTable';
import { GetterApiEndpointNames, RecordType } from './types';
import { CsvExportButtonContainer } from './components/CsvExportButtonContainer';

import './OpDataFetchTable.scss';

export interface OpDataFetchTableRefType {
  resetRowSelections(): void;
}

export type OpDataFetchTableColumn<Endpoint extends GetterApiEndpointNames> =
  OpTableRawColumnType<Utils.SingularOfType<Api.Response[Endpoint]>>;

type OpDataFetchTableProps<
  Endpoint extends GetterApiEndpointNames,
  Selection extends OpTableRecordType[],
> = Omit<
  CommonTableProps<RecordType<Selection>>,
  'dataSource' | 'allowExport'
> & {
  opQueryProps: UseOpQueryOptions<Endpoint, Selection>;
  allowShowHideColumns?: boolean;
  /* forwardRef and generic components do not work together simply. Using a prop ref instead as the simplest workaround.
    This prop ref cannot be named just 'ref' or useImperativeHandle won't work. */
  tableRef?: Ref<OpDataFetchTableRefType>;
  defaultPageSize?: 10 | 25 | 50 | 100 | 200 | 500;
  rowActions?: (
    record: RecordType<Selection>,
    dataSourceLength?: number,
  ) => ReactElement[];
  onRowClick?: (record: RecordType<Selection>) => void;
  allowExport?:
    | boolean
    | Omit<AllowExport<RecordType<Selection>>, 'dataMapCallback'>;
  onFetch?: (data: RecordType<Selection>[]) => void;
};

export const OpDataFetchTable = <
  Endpoint extends GetterApiEndpointNames,
  Selection extends OpTableRecordType[] = Api.Response[Endpoint],
>({
  // Data derived from API queries
  opQueryProps,
  rowKey = 'id',
  columns: rawColumns,
  testId = 'op-data-fetch-table',
  height,
  addButton,
  rowActions,
  onRowClick,
  batchActionMenuItems,
  label,
  allowGlobalSearch = true,
  allowExport = true,
  allowShowHideColumns = true,
  uiStateKey,
  tableRef,
  defaultPageSize = 100,
  onFetch,
  gtm,
  ...tableProps
}: OpDataFetchTableProps<Endpoint, Selection>) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<RecordType<Selection>[]>([]);

  // Ref used to calculate if a row has been clicked, dragged, highlighted, etc
  const rowMouseDownTimestamp = useRef(0);

  // Need to memoize so the useEffect doesn't run repeatedly in useDataFetchTable
  const resetRowSelections = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [setSelectedRowKeys, setSelectedRows]);

  useImperativeHandle(
    tableRef,
    () => ({
      // Allow parent to reset selected rows
      resetRowSelections,
    }),
    [resetRowSelections],
  );

  const {
    data,
    isFetching,
    onChange,
    state,
    debouncedState,
    setState,
    resetState,
    uiKey,
    setPersistedUiState,
  } = useDataFetchTable<Endpoint, Selection, RecordType<Selection>>({
    rawColumns,
    opQueryProps,
    resetRowSelections,
    uiStateKey,
    defaultPageSize,
  });

  const {
    filteredCount = 0,
    totalCount = 0,
    data: jsonData = [],
  } = data?.json || {};

  /** Create dataSource separate from helium data so we can manipulate
   * without mutating the original data */
  const dataSource = useMemo(() => {
    if (Array.isArray(jsonData)) {
      return jsonData as Utils.SingularOfType<Selection, OpTableRecordType>[];
    }
    console.error('Data response needs to be an array for OpDataFetchSelect');
    return [];
  }, [jsonData]);

  // If onFetch is provided, call it with the fetched data
  useEffect(() => {
    if (onFetch && dataSource) {
      onFetch(dataSource);
    }
  }, [onFetch, dataSource]);

  const onShowHideColumns: OnShowHideColumns<RecordType<Selection>> = (
    newColumns,
  ) => {
    setState((prevState) => {
      const newFilters = removeHiddenColumnsFromFilters(
        newColumns,
        prevState.filters,
      );

      const newState = {
        ...prevState,
        default: false,
        columns: newColumns,
        filters: newFilters,
      };

      setPersistedUiState(
        getStateToPersist({
          columns: newState.columns,
          filters: newState.filters,
          sorter: newState.sorter,
        }),
      );

      return newState;
    });
  };

  const tableTitle = () => (
    <TableHeader
      batchActions={
        batchActionMenuItems && (
          <BatchActions
            menuItems={batchActionMenuItems}
            selectedRowKeys={selectedRowKeys}
            selectedRows={selectedRows}
            resetRowSelections={resetRowSelections}
          />
        )
      }
      tableLabel={label}
      globalSearch={
        allowGlobalSearch && (
          <GlobalSearch
            onGlobalSearchChange={({ target: { value } }) => {
              setState((currentState) => ({
                ...currentState,
                pagination: {
                  ...currentState.pagination,
                  current: 1, // Reset to page 1 on filter
                },
                q: value,
              }));

              // Reset row selections on filter
              resetRowSelections();
            }}
          />
        )
      }
      addButton={addButton}
      csvExportButton={
        allowExport && (
          <CsvExportButtonContainer
            gtm={gtm}
            opQueryProps={opQueryProps}
            debouncedState={debouncedState}
            filteredCount={filteredCount}
            selectedRows={selectedRows}
            filename={
              typeof allowExport === 'object' ? allowExport.filename : undefined
            }
            columnsFilterCallback={
              typeof allowExport === 'object'
                ? allowExport.columnsFilterCallback
                : undefined
            }
          />
        )
      }
      showHideColumnsButton={
        allowShowHideColumns && (
          <ShowHideColumnsButton
            tableState={state}
            onShowHideColumns={onShowHideColumns}
            onResetTable={resetState}
          />
        )
      }
    />
  );

  /** Width calculated based on the width of each column (default is 180px)
   * plus the width of the selection column (if there is one). We define the
   * width so that we can constrain the table width to the container width
   * and scroll horizontally */
  const tableWidth = createTableWidth(
    state.columns,
    batchActionMenuItems ? 52 : 0,
  );

  const finalColumns = [...state.columns];

  // Add an action column if rowActions are passed
  /** This is added as the last thing so that it persists and is
   * not part of the show/hide columns logic */
  if (rowActions) {
    let shouldIncludeWidth = true;

    Object.entries(tableProps).forEach(([propName, propValue]) => {
      /**
       * Explicitly passing scroll as undefined or null to turn off scroll thus we dont need to add a width.
       * This width was needed as the scroll prop adds `table-layout: fixed` which we then seemingly needed this for.
       * We should probably take another look at why this is needed at all
       */
      if (propName === 'scroll' && !propValue) {
        shouldIncludeWidth = false;
      }
    });

    finalColumns.push({
      key: 'op-actions',
      render: (_, record) => rowActions(record, dataSource.length),
      ...(shouldIncludeWidth && {
        // @ts-expect-error passing an empty object to row actions just to get the response length
        width: 32 + 32 * rowActions({}).length, // 32 px as a base (16px horizontal padding) and 32 px per action button width looks nice for however many buttons are used.
      }),
      fixed: 'right',
    });
  }

  return (
    <OpTableCore<RecordType<Selection>>
      key={uiKey}
      uiStateKey={uiStateKey}
      loading={isFetching}
      data-testid={testId}
      dataSource={dataSource}
      onChange={onChange}
      columns={finalColumns}
      rowKey={rowKey}
      scroll={{
        y: height,
        x: tableWidth,
      }}
      // Only show table title if there is content to show
      {...((batchActionMenuItems ||
        addButton ||
        allowGlobalSearch ||
        allowExport ||
        allowShowHideColumns) && {
        title: tableTitle,
      })}
      // Allow row selection if batchActionOptions passed
      {...(batchActionMenuItems && {
        rowSelection: {
          selectedRowKeys,
          onChange: (newSelectedRowKeys, newSelectedRows) => {
            setSelectedRowKeys(newSelectedRowKeys);
            setSelectedRows(
              newSelectedRows as Utils.SingularOfType<Selection>[],
            );
          },
          preserveSelectedRowKeys: true,
        },
      })}
      gtm={gtm}
      pagination={false} // We are using custom pagination
      // Only show pagination if there is more than one page
      {...(filteredCount && {
        // eslint-disable-next-line react/no-unstable-nested-components
        footer: () => (
          <TablePagination
            currentPage={state.pagination.current}
            pageSize={state.pagination.pageSize}
            filteredCount={filteredCount}
            totalCount={totalCount}
            setTableState={setState}
          />
        ),
      })}
      // Allow row to be clicked to edit
      onRow={(record) => {
        return {
          className: clsx({
            'ant-table-row--clickable': Boolean(onRowClick),
          }),
          onMouseDown: () => {
            rowMouseDownTimestamp.current = Date.now();
          },
          onClick: () => {
            /**
             * If user clicks a row and doesn't immediately trigger a mouseup
             * don't invoke any of the rowAction callbacks
             */
            if (Date.now() - rowMouseDownTimestamp.current > 150) {
              return;
            }

            onRowClick?.(record);
          },
          'data-gtm': `${gtm}-row-click`,
        };
      }}
      // Pass other props to the table
      {...tableProps}
    />
  );
};
